import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const TILES = [
  {
    to: "/partnerships/teachers",
    label: "Certified teachers",
    blurb: "Independent professionals we trust to teach in our network.",
    image:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=85",
    testid: "partnerships-tile-teachers",
  },
  {
    to: "/partnerships/organizations",
    label: "Organizations",
    blurb: "Schools, foundations and cultural partners that work with us.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=85",
    testid: "partnerships-tile-organizations",
  },
];

/**
 * /partnerships — chooser screen with two large doors.
 * Each door routes into the matching list page.
 */
export default function Partnerships() {
  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6 md:px-10"
      data-testid="partnerships-page"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
            Partnerships
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter font-light text-[#1C1C1C] max-w-3xl">
            People and institutions we work with.
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg text-[#1C1C1C]/70 leading-relaxed">
            Choose a category to see who's part of our network.
          </p>
        </motion.div>

        <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-4 md:gap-6">
          {TILES.map((t, i) => (
            <Tile key={t.to} tile={t} delay={0.15 + i * 0.08} />
          ))}
        </div>
      </div>
    </main>
  );
}

function Tile({ tile, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE, delay }}
    >
      <Link
        to={tile.to}
        data-testid={tile.testid}
        className="group relative block aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden bg-[#1C1C1C]"
      >
        <img
          src={tile.image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 p-7 md:p-10">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/70 font-medium mb-3">
            Browse
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight text-white font-light">
            {tile.label}
          </h2>
          <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed max-w-md">
            {tile.blurb}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-medium text-white/90">
            View list
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
